import { useEffect, useMemo, useRef, useState } from "react";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Select from "react-select";
import SideModal from "/src/components/modal/SideModal/SideModal";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import Button from "/src/components/UI/Button/Button";
import Field from "/src/components/UI/Field/Field";
import useUnitLabels from "/src/hooks/useUnitLabels";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import ConversationCompletedService from "../../services/ConversationCompletedService";
import readApiError from "../../utils/readApiError";
import "./BulkRefreshModal.css";

const POLL_INTERVAL_MS = 3000;
const MAX_UNKNOWN_JOB_POLLS = 4;

const mergeFailureReasons = (jobs) => {
    const merged = {};
    jobs.forEach((job) => (job.failure_reasons || []).forEach((reason) => {
        merged[reason.code] = { ...reason, count: (merged[reason.code]?.count || 0) + reason.count };
    }));
    return Object.values(merged).sort((a, b) => b.count - a.count);
};

const sumJobs = (jobs) => jobs.reduce(
    (total, job) => ({
        total: total.total + job.total,
        remaining: total.remaining + job.remaining,
        done: total.done + job.done,
        failed: total.failed + job.failed,
        cancelled: total.cancelled + (job.cancelled || 0),
    }),
    { total: 0, remaining: 0, done: 0, failed: 0, cancelled: 0 }
);

const summarize = (jobs) => ({ ...sumJobs(jobs), failure_reasons: mergeFailureReasons(jobs), jobIds: jobs.map((job) => job.job_id) });

const toDateInputValue = (offsetDays = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + offsetDays);
    const pad = (value) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Admin tool: re-runs, in the background, every in-progress conversation of a date range (the same
// work as the "Refresh" button of a row). The progress is shown while this window stays open; closing it
// does not stop anything, the server keeps going.
const BulkRefreshModal = NiceModal.create(() => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const toast = useToastDispatchContext();
    const { unitLabels } = useUnitLabels();

    // From yesterday to today by default
    const [dateStart, setDateStart] = useState(() => toDateInputValue(-1));
    const [dateEnd, setDateEnd] = useState(() => toDateInputValue(0));
    const [units, setUnits] = useState([]);
    const [phase, setPhase] = useState("form"); // form | checking | ready | starting | running | error
    const [summary, setSummary] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const [progress, setProgress] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [failureDetails, setFailureDetails] = useState(null); // null = not loaded yet
    const [loadingFailures, setLoadingFailures] = useState(false);
    const cancelledRef = useRef(false);
    const stoppedRef = useRef(false);

    const unitOptions = useMemo(
        () => unitLabels.map((item) => ({ value: item.unit_id, label: item.label })),
        [unitLabels]
    );

    useEffect(() => () => { cancelledRef.current = true; }, []);

    useEffect(() => {
        const resume = async () => {
            const [response, error] = await ConversationCompletedService.getBulkRefreshJobs();
            if (cancelledRef.current || error || !response) return;
            const jobs = response.jobs || [];
            const running = jobs.filter((job) => !job.finished && !job.stalled);
            if (running.length === 0) return;
            setProgress({
                ...summarize(running),
                finished: false,
                jobsCount: running.length,
                stalledCount: jobs.filter((job) => job.stalled).length,
                ...(running.length === 1 ? { date_start: running[0].date_start, date_end: running[0].date_end, started_by: running[0].started_by } : {}),
            });
            setPhase("running");
            followAll(running.map((job) => job.job_id));
        };
        resume();
    }, []);

    const datesValid = Boolean(dateStart && dateEnd && dateEnd >= dateStart);
    const isLocked = phase === "checking" || phase === "starting" || phase === "running";
    const request = () => ({ date_start: dateStart, date_end: dateEnd, unit_ids: units.map((unit) => unit.value) });

    // Any change of the form invalidates the previous count
    const editForm = (setter) => (value) => {
        setter(value);
        setPhase("form");
        setSummary(null);
        setConfirmed(false);
    };

    const fail = (message) => {
        setErrorMessage(message);
        setPhase("error");
        toast.error("Bulk refresh", message);
    };

    const handleCheck = async () => {
        if (!datesValid) return;
        setPhase("checking");
        setErrorMessage("");
        const [response, error] = await ConversationCompletedService.previewBulkRefresh(request());
        if (cancelledRef.current) return;
        if (error || !response) return fail(readApiError(error));
        setSummary(response);
        setConfirmed(false);
        setPhase("ready");
    };

    const followProgress = async (jobId) => {
        let unknownJobCount = 0;
        while (!cancelledRef.current) {
            const [response, error] = await ConversationCompletedService.getBulkRefreshProgress(jobId);
            if (cancelledRef.current) return;
            if (!error && response) {
                unknownJobCount = 0;
                setProgress({ ...response, jobIds: [jobId] });
                if (response.finished) {
                    if (!stoppedRef.current) {
                        toast.success("Bulk refresh finished", response.failed ? `${response.failed} conversation(s) failed.` : "Every conversation has been re-analyzed.");
                    }
                    return;
                }
            } else if (error?.status === 404) {
                unknownJobCount += 1;
                if (unknownJobCount >= MAX_UNKNOWN_JOB_POLLS) {
                    return fail("The progress of this refresh is no longer available. The conversations already queued keep being processed: wait for them before starting another refresh, or they would be done twice.");
                }
            }
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
    };

    const followAll = async (jobIds) => {
        while (!cancelledRef.current) {
            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
            const [response, error] = await ConversationCompletedService.getBulkRefreshJobs();
            if (cancelledRef.current) return;
            if (error || !response) continue;

            const followed = (response.jobs || []).filter((job) => jobIds.includes(job.job_id));
            if (followed.length === 0) continue;

            const finished = followed.every((job) => job.finished || job.stalled);
            setProgress((previous) => ({ ...previous, ...summarize(followed), finished }));
            if (finished) {
                if (!stoppedRef.current) {
                    toast.success("Bulk refresh finished", "Every conversation has been handled.");
                }
                return;
            }
        }
    };

    const handleStart = async () => {
        if (!summary || !confirmed) return;
        setPhase("starting");
        const [response, error] = await ConversationCompletedService.startBulkRefresh(request());
        if (cancelledRef.current) return;
        if (error || !response?.job_id) return fail(readApiError(error));

        setProgress({ total: response.queued, remaining: response.queued, failed: 0, done: 0, finished: response.queued === 0, jobIds: [response.job_id] });
        setPhase("running");
        followProgress(response.job_id);
    };

    const handleStop = async () => {
        const result = await NiceModal.show(ApiModal, {
            title: "Stop the refresh",
            body: "Stop the refresh that is running? The conversations being analyzed right now finish, the others are dropped: they can be refreshed again later.",
            api: { request: ConversationCompletedService.cancelBulkRefresh, param: [] },
            buttons: [
                { className: "validate", label: "Keep going", style: { marginRight: "20px" } },
                { className: "cancel", label: "Stop", type: "submit" },
            ],
        });
        if (cancelledRef.current || !result) return;

        stoppedRef.current = true;
        setProgress((previous) => ({ ...previous, remaining: 0, cancelled: (previous?.cancelled || 0) + (result.dropped || 0), finished: true, stopped: true }));
        toast.success("Bulk refresh stopped", `${result.dropped || 0} conversation(s) dropped.`);
    };

    const handleShowFailures = async () => {
        if (!progress?.jobIds?.length) return;
        setLoadingFailures(true);
        const all = [];
        for (const jobId of progress.jobIds) {
            const [response, error] = await ConversationCompletedService.getBulkRefreshFailures(jobId);
            if (!error && response?.failures) all.push(...response.failures);
        }
        if (cancelledRef.current) return;
        setFailureDetails(all);
        setLoadingFailures(false);
    };

    const handleRestart = () => {
        setFailureDetails(null);
        stoppedRef.current = false;
        setPhase("form");
        setSummary(null);
        setConfirmed(false);
        setProgress(null);
    };

    const finished = phase === "running" && progress?.finished;
    const handled = progress ? progress.done + progress.failed : 0;
    const percent = progress?.total ? Math.round((handled / progress.total) * 100) : 100;

    return (
        <SideModal id={modal.id} autoClose={false} onClose={onClose} style={{ width: "550px" }} name="bulk_refresh_modal">
            <div className="bulk-refresh-leave">
                <div className="button-leave" id="close_modal_container" onClick={onClose}>
                    <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
                </div>
            </div>

            <div className="bulk-refresh">
                <h2><i className="fas fa-rotate"></i> Refresh in bulk</h2>
                <p className="bulk-refresh__intro">
                    Runs again, over a period, every conversation still <strong>in progress</strong> (stuck or never analyzed):
                    the ones with a transcript are re-analyzed with the criteria of their current scorecard, the others
                    are transcribed again from their audio.
                </p>

                {phase !== "running" && (
                    <div className="bulk-refresh__form">
                        <div className="bulk-refresh__dates">
                            <Field label={<span>From <span className="obligatory_field">*</span></span>} title="From">
                                <input className="input full-width" type="date" value={dateStart} disabled={isLocked} onChange={(event) => editForm(setDateStart)(event.target.value)} />
                            </Field>
                            <Field label={<span>To (included) <span className="obligatory_field">*</span></span>} title="To">
                                <input className="input full-width" type="date" value={dateEnd} disabled={isLocked} onChange={(event) => editForm(setDateEnd)(event.target.value)} />
                            </Field>
                        </div>
                        {dateStart && dateEnd && dateEnd < dateStart && (
                            <div className="bulk-refresh__error">The end date is before the start date.</div>
                        )}

                        <Field label="Units (all if empty)" title="Units">
                            <Select
                                classNamePrefix="criteria-select"
                                isMulti
                                options={unitOptions}
                                value={units}
                                onChange={(selected) => editForm(setUnits)(selected || [])}
                                isDisabled={isLocked}
                                placeholder="All units"
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 20000 }) }}
                            />
                        </Field>

                        <div className="bulk-refresh__actions">
                            <Button
                                className="validate"
                                disabled={!datesValid || isLocked}
                                onClick={handleCheck}
                                label={phase === "checking" ? "Counting..." : "Count the conversations"}
                            />
                        </div>
                    </div>
                )}

                {(phase === "ready" || phase === "starting") && summary && (
                    <div className="bulk-refresh__summary">
                        <div className="bulk-refresh__figure">
                            <strong>{summary.to_refresh}</strong>
                            <span>conversation{summary.to_refresh === 1 ? "" : "s"} to refresh</span>
                        </div>
                        <ul>
                            <li>{summary.has_transcript} re-analyzed from their transcript</li>
                            <li>{summary.to_transcribe} transcribed again from their audio</li>
                            {summary.skipped > 0 && <li className="is-muted">{summary.skipped} skipped (no transcript and no audio)</li>}
                        </ul>

                        {summary.to_refresh > 0 ? (
                            <>
                                <label className="bulk-refresh__confirm">
                                    <input type="checkbox" checked={confirmed} disabled={phase === "starting"} onChange={(event) => setConfirmed(event.target.checked)} />
                                    <span>I understand that these {summary.to_refresh} conversations will be analyzed again by the AI (this has a cost).</span>
                                </label>
                                <div className="bulk-refresh__actions">
                                    <Button
                                        className="validate"
                                        disabled={!confirmed || phase === "starting"}
                                        onClick={handleStart}
                                        label={phase === "starting" ? "Starting..." : `Refresh ${summary.to_refresh} conversation${summary.to_refresh === 1 ? "" : "s"}`}
                                    />
                                </div>
                            </>
                        ) : (
                            <p className="bulk-refresh__hint">Nothing to refresh for this period.</p>
                        )}
                    </div>
                )}

                {phase === "running" && progress && (
                    <div className="bulk-refresh__progress">
                        <div className="bulk-refresh__progress-head">
                            {finished ? <i className="fas fa-check-circle is-done"></i> : <i className="fas fa-spinner fa-spin"></i>}
                            <strong>{finished ? "Finished" : "Refreshing..."}</strong>
                            <span>{handled} / {progress.total}</span>
                        </div>
                        {progress.jobsCount > 1 && (
                            <p className="bulk-refresh__hint">{progress.jobsCount} refreshes are running: the figures below add them up.</p>
                        )}
                        {progress.stalledCount > 0 && (
                            <p className="bulk-refresh__hint">
                                {progress.stalledCount} older refresh{progress.stalledCount > 1 ? "es" : ""} stopped moving (tasks lost, for example during a server change) and {progress.stalledCount > 1 ? "are" : "is"} ignored here.
                            </p>
                        )}
                        {progress.date_start && (
                            <p className="bulk-refresh__hint">
                                Period {progress.date_start} to {progress.date_end}{progress.started_by ? `, started by ${progress.started_by}` : ""}
                            </p>
                        )}
                        <div className="bulk-refresh__bar"><span style={{ width: `${percent}%` }} /></div>
                        <div className="bulk-refresh__counts">
                            <div><strong>{progress.remaining}</strong><span>remaining</span></div>
                            <div><strong>{progress.done}</strong><span>done</span></div>
                            <div className={progress.failed ? "has-failed" : ""}><strong>{progress.failed}</strong><span>failed</span></div>
                        </div>
                        {!finished && (
                            <>
                                <p className="bulk-refresh__hint">
                                    You can close this window: the refresh goes on in the background.
                                </p>
                                <div className="bulk-refresh__actions">
                                    <Button className="cancel" label="Stop the refresh" onClick={handleStop} />
                                </div>
                            </>
                        )}
                        {finished && progress.cancelled > 0 && (
                            <p className="bulk-refresh__hint">
                                Stopped: {progress.cancelled} conversation{progress.cancelled > 1 ? "s were" : " was"} dropped. A new refresh will pick them up again.
                            </p>
                        )}
                        {progress.failed > 0 && (
                            <div className="bulk-refresh__failures">
                                <strong>Why {progress.failed} conversation{progress.failed > 1 ? "s" : ""} failed</strong>
                                {(progress.failure_reasons || []).length === 0 && (
                                    <p className="bulk-refresh__hint">No reason was recorded for these ones.</p>
                                )}
                                <ul>
                                    {(progress.failure_reasons || []).map((reason) => (
                                        <li key={reason.code}><span className="bulk-refresh__failures-count">{reason.count}</span> {reason.message}</li>
                                    ))}
                                </ul>
                                {failureDetails === null && (
                                    <Button className="validate" label={loadingFailures ? "Loading..." : "Show the conversations"} disabled={loadingFailures} onClick={handleShowFailures} />
                                )}
                                {failureDetails !== null && (
                                    <ul className="bulk-refresh__failures-list">
                                        {failureDetails.map((failure) => (
                                            <li key={failure.call_id} title={failure.message}><code>{failure.call_id}</code> {failure.message}</li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        {finished && progress.failed > 0 && (
                            <p className="bulk-refresh__hint">
                                The failed ones stay as they were: refresh them again from the list (Refresh button).
                            </p>
                        )}
                        {finished && (
                            <div className="bulk-refresh__actions">
                                <Button className="validate" label="Start another refresh" onClick={handleRestart} />
                            </div>
                        )}
                    </div>
                )}

                {phase === "error" && <div className="bulk-refresh__error">{errorMessage}</div>}
            </div>
        </SideModal>
    );
});

export default BulkRefreshModal;
